"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.CRM_SYSTEM_ROLE_STAGE_IDS = exports.RETIRED_CRM_SUB_STAGE_IDS = exports.CRM_STAGE_COLOR_TOKENS = exports.CRM_STAGE_SYSTEM_ROLES = void 0;
exports.buildDefaultCrmPipeline = buildDefaultCrmPipeline;
exports.resolveCrmStage = resolveCrmStage;
exports.crmStageFieldsForRole = crmStageFieldsForRole;
exports.validateCrmPipeline = validateCrmPipeline;
exports.readCrmPipelineDocument = readCrmPipelineDocument;
const crm_stages_model_1 = require("./prompts/crm-stages.model");
/**
 * Stages the system itself writes or checks. Code resolves these through
 * `resolveCrmStage` instead of literal IDs, so admins can rename and reorder
 * stages without breaking intake, scheduling, the portal or automations.
 */
exports.CRM_STAGE_SYSTEM_ROLES = ['entry', 'scheduled', 'waiting_report', 'lost'];
exports.CRM_STAGE_COLOR_TOKENS = ['sky', 'amber', 'violet', 'fuchsia', 'teal', 'emerald', 'rose', 'slate'];
// Default roles and colors pin today's hardcoded IDs, so existing deals keep their meaning.
const DEFAULT_STAGE_ROLES = new Map([
    [crm_stages_model_1.MainStageType.WAITING_FOR_REPORT, 'waiting_report'],
    [crm_stages_model_1.MainStageType.NOT_INTERESTED, 'lost'],
]);
const DEFAULT_SUB_STAGE_ROLES = new Map([
    ['new_lead', 'entry'],
    ['inspection_scheduled', 'scheduled'],
]);
exports.RETIRED_CRM_SUB_STAGE_IDS = [
    'inspection_completed',
    'no_answer_multiple',
    'in_chat',
    'after_quote',
    'closed_customer',
];
const RETIRED_CRM_SUB_STAGE_ID_SET = new Set(exports.RETIRED_CRM_SUB_STAGE_IDS);
function withoutRetiredSubStages(subStages) {
    return subStages.filter((subStage) => !RETIRED_CRM_SUB_STAGE_ID_SET.has(subStage.id));
}
/**
 * Each role is pinned to one stage ID in every pipeline. Stored deals, the portal,
 * conversion analytics and notification preferences check these IDs directly, so a
 * role may move between main stages but never onto a different stage ID.
 */
exports.CRM_SYSTEM_ROLE_STAGE_IDS = new Map([...DEFAULT_STAGE_ROLES, ...DEFAULT_SUB_STAGE_ROLES].map(([id, role]) => [role, id]));
const DEFAULT_STAGE_COLORS = new Map([
    [crm_stages_model_1.MainStageType.NEW, 'sky'],
    [crm_stages_model_1.MainStageType.FOLLOW_UP, 'amber'],
    [crm_stages_model_1.MainStageType.QUOTE, 'violet'],
    [crm_stages_model_1.MainStageType.SPECIAL_CASES, 'fuchsia'],
    [crm_stages_model_1.MainStageType.WAITING_FOR_REPORT, 'teal'],
    [crm_stages_model_1.MainStageType.NOT_INTERESTED, 'rose'],
]);
const byOrder = (a, b) => a.order - b.order;
function withSubStageRole(sub) {
    const systemRole = DEFAULT_SUB_STAGE_ROLES.get(sub.id);
    return systemRole ? { id: sub.id, name: sub.name, order: sub.order, systemRole } : { id: sub.id, name: sub.name, order: sub.order };
}
/**
 * Seed a pipeline from `STAGE_HIERARCHY`, applying the legacy per-company
 * sub-stage edits from `crmConfig/stages`. Role sub-stages the legacy edits
 * dropped are appended so the result always passes `validateCrmPipeline`.
 */
function buildDefaultCrmPipeline(clientType, legacySubStages) {
    const stages = crm_stages_model_1.STAGE_HIERARCHY.slice()
        .sort(byOrder)
        .map((main) => {
        const defaults = withoutRetiredSubStages(main.subStages).slice().sort(byOrder);
        const edited = legacySubStages?.[main.id]
            ? withoutRetiredSubStages(legacySubStages[main.id]).slice().sort(byOrder)
            : undefined;
        const editedIds = new Set(edited?.map((sub) => sub.id));
        const droppedRoleSubs = edited ? defaults.filter((sub) => DEFAULT_SUB_STAGE_ROLES.has(sub.id) && !editedIds.has(sub.id)) : [];
        const subStages = [...(edited ?? defaults), ...droppedRoleSubs].map((sub, index) => withSubStageRole({ ...sub, order: index + 1 }));
        const systemRole = DEFAULT_STAGE_ROLES.get(main.id);
        return {
            id: main.id,
            name: main.name,
            order: main.order,
            color: DEFAULT_STAGE_COLORS.get(main.id) ?? 'slate',
            ...(systemRole ? { systemRole } : {}),
            subStages,
        };
    });
    return { clientType, stages };
}
function resolveCrmStage(pipeline, role) {
    for (const stage of pipeline.stages) {
        if (stage.systemRole === role)
            return { mainStageId: stage.id };
        const sub = stage.subStages.find((candidate) => candidate.systemRole === role);
        if (sub)
            return { mainStageId: stage.id, subStageId: sub.id };
    }
    throw new Error(`CRM pipeline ${pipeline.clientType} has no stage with system role ${role}`);
}
/**
 * Deal fields for a role stage, including the legacy flat `stageId` older readers
 * still use, and the stage's current display name for activity entries.
 */
function crmStageFieldsForRole(pipeline, role) {
    const stage = resolveCrmStage(pipeline, role);
    const main = pipeline.stages.find((candidate) => candidate.id === stage.mainStageId);
    const sub = main?.subStages.find((candidate) => candidate.id === stage.subStageId);
    return { stage, stageId: stage.subStageId ?? stage.mainStageId, stageName: (sub ?? main)?.name ?? '' };
}
/** Returns human-readable problems; an empty array means the pipeline can be saved. */
function validateCrmPipeline(pipeline) {
    const errors = [];
    const seenIds = new Set();
    const seenRoles = new Set();
    const entries = pipeline.stages.flatMap((stage) => [stage, ...stage.subStages]);
    for (const entry of entries) {
        if (seenIds.has(entry.id))
            errors.push(`duplicate stage id: ${entry.id}`);
        seenIds.add(entry.id);
        if (!entry.name.trim())
            errors.push(`blank stage name: ${entry.id}`);
        if (!entry.systemRole)
            continue;
        if (seenRoles.has(entry.systemRole))
            errors.push(`duplicate system role: ${entry.systemRole}`);
        const pinnedId = exports.CRM_SYSTEM_ROLE_STAGE_IDS.get(entry.systemRole);
        if (entry.id !== pinnedId)
            errors.push(`system role ${entry.systemRole} must stay on stage ${pinnedId}`);
        seenRoles.add(entry.systemRole);
    }
    for (const role of exports.CRM_STAGE_SYSTEM_ROLES) {
        if (!seenRoles.has(role))
            errors.push(`missing system role: ${role}`);
    }
    return errors;
}
/**
 * Turns a stored `crmPipelines/{clientType}` document into a usable pipeline.
 * A missing or invalid document yields the default seeded from the legacy
 * sub-stage config; `errors` is non-empty when a stored document was rejected.
 */
function readCrmPipelineDocument(clientType, stored, legacySubStages) {
    if (stored === undefined)
        return { pipeline: buildDefaultCrmPipeline(clientType, legacySubStages), errors: [] };
    const stages = stored?.stages;
    const shaped = Array.isArray(stages)
        && stages.every((stage) => Array.isArray(stage?.subStages));
    const sanitized = shaped
        ? {
            ...stored,
            clientType,
            stages: stored.stages.map((stage) => ({
                ...stage,
                subStages: withoutRetiredSubStages(stage.subStages),
            })),
        }
        : undefined;
    const errors = sanitized ? validateCrmPipeline(sanitized) : ['malformed pipeline document'];
    if (errors.length > 0)
        return { pipeline: buildDefaultCrmPipeline(clientType, legacySubStages), errors };
    return { pipeline: sanitized, errors };
}
